import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConvertToCustomerButton } from "@/components/ConvertToCustomerButton";

const STATUSES = ["new", "contacted", "closed"];
const TONES = {
  new: "bg-brand-ice/30 text-blue-900",
  contacted: "bg-amber-100 text-amber-800",
  closed: "bg-zinc-100 text-zinc-600",
};

const LeadRow = ({ lead, saving, updateStatus }) => (
  <li data-testid={`admin-lead-${lead.id}`} className="py-3 first:pt-0 last:pb-0">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="font-medium text-brand-ink">{lead.name} <span className="text-zinc-500 font-normal">· {lead.phone}</span></p>
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-500">{lead.created_at?.slice(0, 10)}</span>
        <span data-testid={`admin-lead-status-badge-${lead.id}`}
          className={`text-xs font-bold px-2 py-0.5 rounded-sm uppercase tracking-wide ${TONES[lead.status] || TONES.new}`}>
          {lead.status}
        </span>
        {saving === lead.id ? (
          <Loader2 className="w-4 h-4 text-brand animate-spin" />
        ) : (
          <Select value={lead.status} onValueChange={(v) => updateStatus(lead.id, v)}>
            <SelectTrigger data-testid={`admin-lead-status-select-${lead.id}`} className="rounded-sm h-8 w-32 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
    <p className="text-sm text-zinc-600 mt-0.5">{lead.service || "General"} {lead.city ? `· ${lead.city}` : ""} · {lead.email}</p>
    {lead.message && <p className="text-sm text-zinc-500 mt-0.5">{lead.message}</p>}
    <ConvertToCustomerButton name={lead.name} email={lead.email} phone={lead.phone} testId={`admin-lead-convert-${lead.id}`} />
  </li>
);

export const AdminLeads = () => {
  const [leads, setLeads] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/admin/leads").then((r) => setLeads(r.data.leads)).catch(() => setLeads([]));
  }, []);

  const updateStatus = async (id, status) => {
    setSaving(id);
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Lead marked ${status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  let body;
  if (leads === null) {
    body = <Loader2 className="w-6 h-6 text-brand animate-spin" />;
  } else if (leads.length === 0) {
    body = <p data-testid="admin-leads-empty" className="text-sm text-zinc-600">No leads yet.</p>;
  } else {
    body = (
      <ul className="divide-y divide-zinc-100">
        {leads.map((l) => <LeadRow key={l.id} lead={l} saving={saving} updateStatus={updateStatus} />)}
      </ul>
    );
  }

  return (
    <div data-testid="admin-leads-card" className="bg-white border border-zinc-200 p-6 rounded-sm mt-6">
      <h2 className="font-heading font-bold text-lg text-brand-ink mb-4">Recent Leads (Admin)</h2>
      {body}
    </div>
  );
};
